import { addDays, eachDayOfRange, formatEventTime, parseDate } from '@/lib/utils/calendar';
import { formatDateKey, getHolidayName } from '@/lib/utils/holidays';

/**
 * 대화 안에 뜨는 회의 달력 — **물어본 기간만** 그린다 (DOC-23 §11).
 *
 * `MonthlyCalendar`(월간회의 화면)를 그대로 쓰지 않은 이유는 그쪽이 편집 달력이기 때문이다.
 * 칸을 더블클릭하면 일정이 만들어지고, 한 칸이 160px 이고, 언제나 **한 달 전체**를 그린다.
 * 대화에서 "오늘 회의 뭐 있어" 라고 물었는데 8월 한 장이 통째로 뜨면, 답이 아니라 화면 이동이다.
 *
 * 그래서 규칙을 하나만 바꿨다 — **물어본 구간이 걸친 주(週)만** 그린다.
 *
 *     "오늘"      → 한 줄
 *     "이번주"    → 한 줄
 *     "이번달"    → 다섯~여섯 줄
 *     기간 없음   → 앞으로 30일이 걸친 주만 (달을 넘어가도 이어서 그린다)
 *
 * 빈 달을 붙이지 않으므로 대화 흐름을 끊지 않고, 답변 문장과 같은 값을 같은 범위로 보여 준다.
 *
 * 누를 것은 없다. 조회 결과이지 초안이 아니다(`views.ts`).
 */

const WEEKDAY_LABELS = ['일', '월', '화', '수', '목', '금', '토'];

/** scope 별 색. 월간회의 화면의 구분(본사/현장 주요일정/현장 회의일정)을 그대로 따른다. */
const SCOPE_COLOR: Record<string, string> = {
  HQ: '#2563eb',
  FIELD: '#16a34a',
  SITE: '#d97706',
};

export type MeetingOccurrence = {
  date: string;
  time: string | null;
  title: string;
  scope: string;
  site: string | null;
  notes: string | null;
};

export type MeetingCalendarPayload = {
  label: string;
  start: string;
  end: string;
  occurrences: MeetingOccurrence[];
  omitted: number;
};

export function MeetingCalendarCard({ payload }: { payload: MeetingCalendarPayload }) {
  const { label, start, end, occurrences, omitted } = payload;
  if (!occurrences.length) return null;

  // 구간이 걸친 주만. 시작 주의 일요일 ~ 끝 주의 토요일.
  const rangeStart = addDays(parseDate(start), -parseDate(start).getDay());
  const rangeEnd = addDays(parseDate(end), 6 - parseDate(end).getDay());
  const days = eachDayOfRange(rangeStart, rangeEnd);

  const byDate = new Map<string, MeetingOccurrence[]>();
  for (const occurrence of occurrences) {
    byDate.set(occurrence.date, [...(byDate.get(occurrence.date) ?? []), occurrence]);
  }

  const todayKey = formatDateKey(new Date());
  const inRange = (key: string) => key >= start && key <= end;

  return (
    <div className="card" style={{ padding: '12px', marginTop: '10px', overflow: 'hidden' }}>
      <p className="text-xs font-semibold mb-2" style={{ color: 'var(--text-secondary)' }}>
        📅 {label}
      </p>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, minmax(0, 1fr))', gap: '1px' }}>
        {WEEKDAY_LABELS.map((weekday, index) => (
          <div
            key={weekday}
            className="text-center"
            style={{
              fontSize: '11px',
              fontWeight: 700,
              padding: '4px 0',
              color: index === 0 ? '#dc2626' : index === 6 ? '#2563eb' : 'var(--text-muted)',
            }}
          >
            {weekday}
          </div>
        ))}

        {days.map((date) => {
          const dateKey = formatDateKey(date);
          const dayOccurrences = byDate.get(dateKey) ?? [];
          const holiday = getHolidayName(date);
          // 물어본 구간 밖의 칸(주를 채우려고 붙은 날)은 흐리게. 지우지는 않는다 —
          // 요일 줄이 어긋나면 달력으로 읽히지 않는다.
          const outside = !inRange(dateKey);

          return (
            <div
              key={dateKey}
              style={{
                minHeight: '52px',
                padding: '3px 4px',
                borderRadius: '4px',
                background: outside ? 'transparent' : holiday ? '#fff7ed' : 'var(--bg-hover)',
                opacity: outside ? 0.35 : 1,
                boxShadow: dateKey === todayKey ? 'inset 0 0 0 1.5px #dc2626' : 'none',
              }}
            >
              <p
                style={{
                  fontSize: '11px',
                  fontWeight: dayOccurrences.length ? 700 : 500,
                  color: holiday || date.getDay() === 0 ? '#dc2626' : date.getDay() === 6 ? '#2563eb' : 'var(--text-secondary)',
                }}
              >
                {date.getDate()}
              </p>

              {dayOccurrences.map((occurrence, index) => (
                <p
                  key={`${dateKey}:${index}`}
                  className="truncate"
                  title={[occurrence.title, occurrence.site, occurrence.notes].filter(Boolean).join(' · ')}
                  style={{
                    fontSize: '10px',
                    lineHeight: 1.35,
                    marginTop: '2px',
                    paddingLeft: '3px',
                    borderLeft: `2px solid ${SCOPE_COLOR[occurrence.scope] ?? 'var(--text-muted)'}`,
                    color: 'var(--text-primary)',
                  }}
                >
                  {formatEventTime(occurrence.time) ? `${formatEventTime(occurrence.time)} ` : ''}
                  {occurrence.site ?? occurrence.title}
                </p>
              ))}
            </div>
          );
        })}
      </div>

      {omitted > 0 ? (
        <p className="text-xs mt-2" style={{ color: 'var(--text-muted)' }}>
          … 외 {omitted}건은 달력에 담지 못했습니다.
        </p>
      ) : null}
    </div>
  );
}
